import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { CustomerAuthForm } from "../components/customer/CustomerAuthForm";
import {
  User, ShoppingCart, CalendarCheck, Clock, Package, Truck, CheckCircle2,
  XCircle, ArrowLeft, Eye, LogOut, Mail, Phone as PhoneIcon, MapPin
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_509254e3-4fbd-4a68-8194-b9dcfb6ff3e4/artifacts/bylh1er6_Image.jpg";

const STATUS_CONFIG = {
  new: { label: "Noua", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Clock },
  confirmed: { label: "Confirmata", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: CheckCircle2 },
  preparing: { label: "In pregatire", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: Package },
  out_for_delivery: { label: "In livrare", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Truck },
  delivered: { label: "Livrata", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  cancelled: { label: "Anulata", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
};

export default function CustomerAccountPage() {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [tab, setTab] = useState("orders");
  const isLoggedIn = !!customer;

  const applyAccount = (data) => {
    setCustomer(data.customer);
    setOrders(data.orders || []);
    setReservations(data.reservations || []);
  };

  const handleLogout = async () => {
    await fetch(`${API}/api/public/customer/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setCustomer(null);
    setOrders([]);
    setReservations([]);
  };

  useEffect(() => {
    localStorage.removeItem("scutersh_customer");
    if (location.state?.account) { applyAccount(location.state.account); setChecking(false); return; }
    fetch(`${API}/api/public/customer/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) applyAccount(data); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [location.state]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/boutique" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Inapoi la magazin
          </Link>
          <Link to="/boutique" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Scutersh" className="w-8 h-8 rounded object-contain" />
            <span className="font-bold text-sm">SCUTERSH</span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {checking ? (
          <div className="text-center py-20 text-gray-500" data-testid="account-checking">...</div>
        ) : !isLoggedIn ? (
          <CustomerAuthForm onLoggedIn={applyAccount} />
        ) : (
          /* Account Dashboard */
          <div className="space-y-6" data-testid="customer-dashboard">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold" data-testid="customer-greeting">Bună, {customer?.first_name || customer?.name}!</h1>
                <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                  {customer?.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{customer.email}</span>}
                  {customer?.phone && <span className="flex items-center gap-1"><PhoneIcon className="w-3.5 h-3.5" />{customer.phone}</span>}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="text-gray-400 border-white/10" data-testid="btn-logout-customer">
                <LogOut className="w-4 h-4 mr-1" /> Deconectare
              </Button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
              {[
                { id: "orders", label: "Comenzi", icon: ShoppingCart, count: orders.length },
                { id: "reservations", label: "Rezervari", icon: CalendarCheck, count: reservations.length },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium flex-1 justify-center transition-colors ${
                    tab === t.id ? "bg-emerald-600 text-white" : "text-gray-400 hover:text-white"
                  }`}
                  data-testid={`customer-tab-${t.id}`}
                >
                  <t.icon className="w-4 h-4" />
                  {t.label} ({t.count})
                </button>
              ))}
            </div>

            {/* Orders */}
            {tab === "orders" && (
              <div className="space-y-3">
                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500">Nicio comanda inca</p>
                    <Link to="/boutique">
                      <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">Viziteaza magazinul</Button>
                    </Link>
                  </div>
                ) : (
                  orders.map(o => {
                    const cfg = STATUS_CONFIG[o.status] || STATUS_CONFIG.new;
                    const StatusIcon = cfg.icon;
                    return (
                      <Card key={o.order_id} className="bg-white/5 border-white/10" data-testid={`customer-order-${o.order_id}`}>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <StatusIcon className="w-4 h-4 text-gray-400" />
                              <span className="font-mono text-sm font-bold">{o.order_id}</span>
                              <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>{cfg.label}</Badge>
                            </div>
                            <span className="text-xs text-gray-500">
                              {o.created_at ? new Date(o.created_at).toLocaleDateString("ro-RO") : "—"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-sm">{o.item_name || "N/A"}</p>
                            <span className="font-bold text-emerald-400">{o.total_eur?.toFixed(0)} EUR</span>
                          </div>
                          {o.tracking_code && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 font-mono">Tracking: {o.tracking_code}</span>
                              <Link to={`/track/${o.tracking_code}`}>
                                <Button variant="outline" size="sm" className="text-xs h-7 border-white/10 text-emerald-400">
                                  <Eye className="w-3 h-3 mr-1" /> Urmareste
                                </Button>
                              </Link>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <MapPin className="w-3 h-3" />
                            <span>{o.delivery_address}, {o.city} ({o.zone || "N/A"})</span>
                            <span>— Livrare: {o.delivery_fee_eur || 0} EUR</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}

            {/* Reservations */}
            {tab === "reservations" && (
              <div className="space-y-3">
                {reservations.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarCheck className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500">Nicio rezervare inca</p>
                  </div>
                ) : (
                  reservations.map(r => {
                    const statusMap = {
                      active: { label: "Activa", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
                      confirmed: { label: "Confirmata", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
                      cancelled: { label: "Anulata", color: "bg-red-500/20 text-red-400 border-red-500/30" },
                      expired: { label: "Expirata", color: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
                    };
                    const cfg = statusMap[r.status] || statusMap.active;
                    return (
                      <Card key={r.reservation_id} className="bg-white/5 border-white/10" data-testid={`customer-reservation-${r.reservation_id}`}>
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CalendarCheck className="w-4 h-4 text-yellow-400" />
                              <span className="font-mono text-sm font-bold">{r.reservation_id}</span>
                              <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>{cfg.label}</Badge>
                            </div>
                            <span className="text-xs text-gray-500">
                              {r.created_at ? new Date(r.created_at).toLocaleDateString("ro-RO") : "—"}
                            </span>
                          </div>
                          <p className="text-sm">{r.scooter_name} — <span className="font-bold text-emerald-400">{r.scooter_price} EUR</span></p>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
